/** Remove all non-numerical values from a string */
export function formatOnlyDigits(value: string) {
  return value.replace(/\D/g, "");
}

/**
 * Builds the API phone payload for personal-info updates.
 * Returns undefined when blank (optional). Non-US countries must not submit a number
 * (UI validation blocks that); if somehow called, returns undefined rather than store.
 */
export function toOptionalUsApiPhoneNumber(
  phoneCountryCode: string | undefined,
  nationalNumber: string | undefined,
): string | undefined {
  const trimmed = nationalNumber?.trim() ?? "";
  if (!trimmed) {
    return undefined;
  }
  if (phoneCountryCode && phoneCountryCode !== "US") {
    return undefined;
  }
  const digits = formatOnlyDigits(trimmed);
  return digits || undefined;
}

/**
 * Formats a phone number with country code into a standardized format
 * Examples:
 * "+15558675309" -> "+1 (555) 867-5309"
 * "14079274425" -> "+1 (407) 927-4425"
 */
export function formatPhoneNumberWithCountryCode(value: string): string {
  if (!value) {
    return "";
  }

  // Remove all non-digits first
  const digits = value.replace(/\D/g, "");

  // If we have 11 digits and it starts with 1, treat it as a US number
  if (digits.length === 11 && digits.startsWith("1")) {
    const countryCode = "+1";
    const baseNumber = digits.slice(1);
    const formattedBase = `(${baseNumber.slice(0, 3)}) ${baseNumber.slice(3, 6)}-${baseNumber.slice(6, 10)}`;
    return `${countryCode} ${formattedBase}`;
  }

  // If we have 10 digits, format it without country code
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }

  // Return original if we don't match any expected format
  return value;
}

/**
 * Builds a normalized `tel:` href for click-to-call. Returns undefined when there are no digits.
 * US numbers: 10 digits → `tel:+1…`; 11 digits starting with 1 → `tel:+…`.
 * Other digit strings: `tel:+` + digits (international).
 */
export function phoneToTelHref(value: string | null | undefined): string | undefined {
  if (value == null || value.trim() === "") {
    return undefined;
  }
  const digits = formatOnlyDigits(value);
  if (!digits) {
    return undefined;
  }
  if (digits.length === 10) {
    return `tel:+1${digits}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return `tel:+${digits}`;
  }
  return `tel:+${digits}`;
}
